import { smaf5CalculatorKeys as k } from "@/i18n/smaf5-calculator-keys"

/**
 * sMAF-5: the simplified Metabolic dysfunction Associated Fibrosis-5 score, a
 * non-invasive estimate of the risk that a liver already carries fibrosis,
 * built from five things a routine check-up produces.
 *
 *   sMAF-5 = -15.773
 *          + 0.247  x max(BMI, 25)
 *          + 2.339  if diabetes
 *          + 2.95   x ln(AST)
 *          - 0.00686 x platelets
 *          + 0.503  if male
 *
 * Source: van Kleef LA, Jin X, Yip TC-F et al., "Simplified metabolic
 * dysfunction associated fibrosis-5 (sMAF-5) score to identify at-risk liver
 * fibrosis and to predict prognosis", Journal of Hepatology 2026;84(Suppl
 * 1):S621-S622, abstract WED-215 at the EASL Congress 2026. The parent score
 * is van Kleef LA et al., Gastroenterology 2024;167:357-367.
 *
 * The abstract names the five terms, the three bands and the outcome rates in
 * each, but prints no coefficients. Those are taken from the authors' own
 * calculator at https://maf5.com (built by van Kleef and Brouwer, the
 * abstract's first and last authors), whose sMAF-5 branch is transcribed above
 * term for term. `__tests__/smaf5-calculator.test.ts` pins every one of them
 * against a longhand reference, because a mistyped coefficient here would
 * still return believable numbers.
 *
 * What sMAF-5 is FOR is the waist. MAF-5 needs a waist circumference and a
 * BMI-by-waist interaction term, which a blood report never carries and few
 * people can supply from memory; sMAF-5 drops both and replaces them with sex,
 * at essentially no cost in discrimination (AUC 0.803 against MAF-5's 0.808,
 * and against FIB-4's 0.667, for detecting liver stiffness of 8 kPa or more).
 * That is why this calculator exists and the MAF-5 one does not: the inputs
 * are the ones a reader actually has.
 *
 * Unlike SCORE2 (lib/score2.ts) there is nothing per-country in it: the
 * coefficients were fitted once, on US NHANES data, and are applied unchanged
 * everywhere, so there is no market map to keep in step with the gate. Like
 * FIB-4 (lib/fib4.ts) the page is CH-only because its copy is.
 *
 * Pure data plus arithmetic, and in `lib/` rather than beside the route so the
 * dependency points the way the rest of the tree does (app -> lib, never
 * back): the widget, the on-page band table AND the markdown mirror in
 * `lib/native-markdown/smaf5-calculator.ts` read this one source, so a changed
 * cut-off cannot drift between them. It is also unit-testable without
 * rendering React.
 */

export type Smaf5Sex = Smaf5SexOption["value"]

/**
 * `as const` is load-bearing here and in `BAND_DEFS` below, for the reason the
 * BMR calculator's `SEX_OPTIONS` documents: annotating `labelKey` as `string`
 * widens it out of the catalog's key union and next-intl's typed `t()` rejects
 * it outright.
 */
const SEX_DEFS = [
  { value: "female", labelKey: k.calcSexFemale },
  { value: "male", labelKey: k.calcSexMale },
] as const

export type Smaf5SexOption = (typeof SEX_DEFS)[number]

export const SEX_OPTIONS: readonly Smaf5SexOption[] = SEX_DEFS

/**
 * Plausibility bounds on the measured inputs. Outside these a value is almost
 * certainly a typo, the wrong unit, or an acute illness the score is not
 * calibrated for.
 *
 * Height and weight match the ones the BMR calculator accepts (lib/bmr.ts).
 * Two calculators on the same site disagreeing about what counts as a
 * plausible height would be a defect in its own right, and these are
 * anthropometric bounds rather than anything this model has an opinion about.
 *
 * AST is in U/L and platelets in G/L (10^9 per litre), the units Swiss
 * laboratories report. There is no unit switcher, for the reason the FIB-4
 * page has none: a converter is the one place a reader can feed a number into
 * a term expecting a different scale and get a wrong answer that still looks
 * plausible.
 */
export const MIN_HEIGHT_CM = 90
export const MAX_HEIGHT_CM = 250
export const MIN_WEIGHT_KG = 20
export const MAX_WEIGHT_KG = 400
export const MIN_AST = 5
export const MAX_AST = 1000
export const MIN_PLATELETS = 10
export const MAX_PLATELETS = 1000

/**
 * Bounds on the BMI the height and weight produce.
 *
 * Checked separately because the two field bounds do not imply it: 40 kg at
 * 190 cm and 300 kg at 150 cm are each a plausible-looking number in a
 * plausible-looking box, and only their ratio gives them away.
 */
export const MIN_BMI = 12
export const MAX_BMI = 70

/**
 * The BMI at which the model's BMI term stops moving downwards.
 *
 * Not a plausibility bound and not ours: the authors' own implementation
 * floors the BMI term at 25 before multiplying it, so every BMI below 25
 * contributes exactly what 25 contributes. sMAF-5 was fitted in people with
 * metabolic dysfunction, where a BMI under 25 is the sparse tail, and the
 * floor is what stops the model extrapolating along a slope it never saw.
 *
 * It is applied inside `smaf5Score` rather than by callers, so it cannot be
 * forgotten at one of the three call sites, and surfaced to the reader through
 * `smaf5IsBmiFloored`: someone with a BMI of 21 is entitled to know their
 * score was computed as though it were 25.
 */
export const BMI_FLOOR = 25

/** The two cut-offs, as the abstract quotes them: below 0, 0 to 1, 1 and up. */
export const LOW_BELOW = 0
export const HIGH_FROM = 1

export interface Smaf5Input {
  sex: Smaf5Sex
  /** kg/m^2, unfloored: `smaf5Score` applies `BMI_FLOOR` itself. */
  bmi: number
  /** Type 2 diabetes, diagnosed. */
  diabetes: boolean
  /** Aspartate aminotransferase (ASAT / GOT), U/L. */
  ast: number
  /** Platelet count, G/L, i.e. 10^9 per litre. */
  platelets: number
}

/** What the form collects, before the BMI is derived from it. */
export interface Smaf5Measurements {
  sex: Smaf5Sex
  heightCm: number
  weightKg: number
  diabetes: boolean
  ast: number
  platelets: number
}

/** BMI from the two fields the form asks for. */
export function bmiFrom(heightCm: number, weightKg: number): number {
  const heightM = heightCm / 100
  return weightKg / (heightM * heightM)
}

/** The BMI as it is shown beside the result: one decimal, as everywhere else. */
export function roundBmi(value: number): number {
  return Math.round(value * 10) / 10
}

/**
 * The published score.
 *
 * A logit, which is why it is not a percentage and why it goes negative: zero
 * and one are the two published cut-offs on that scale, not a floor and a
 * ceiling. Returns the unrounded value; callers display and classify
 * `roundScore()` of it, never this one (see `roundScore`).
 *
 * The two terms worth understanding, because they are the ones a reader will
 * question:
 *
 *   - AST enters through a natural log, so it is the ORDER of magnitude that
 *     counts. Going from 20 to 40 U/L adds about as much as going from 40 to
 *     80, which is why a mildly raised enzyme moves the score much less than
 *     the raw number suggests.
 *   - Platelets subtract, and they are the one term with no floor: the count
 *     falls as scarring stiffens the liver and the spleen holds more back, so
 *     a low count raises the score. It is also why a count entered per
 *     microlitre is so dangerous here (see `smaf5Problem`).
 */
export function smaf5Score({
  sex,
  bmi,
  diabetes,
  ast,
  platelets,
}: Smaf5Input): number {
  return (
    -15.773 +
    0.247 * Math.max(bmi, BMI_FLOOR) +
    (diabetes ? 2.339 : 0) +
    2.95 * Math.log(ast) -
    0.00686 * platelets +
    (sex === "male" ? 0.503 : 0)
  )
}

/**
 * The score as it is shown to the reader: two decimal places.
 *
 * Callers must classify THIS value, not the raw one, so the band always
 * describes the number on screen. The cut-offs are whole numbers here rather
 * than the two-decimal figures FIB-4 quotes, but the rule is the same: a raw
 * 0.996 displays as "1.00", which the table calls high risk, so banding the
 * raw value would print 1.00 under an intermediate label.
 *
 * The `=== 0` line is why this is not a one-liner. Rounding a small negative
 * score yields NEGATIVE zero, and `-0 < 0` is false in JavaScript, so an
 * unnormalised -0 would band as intermediate. That happens to be the right
 * answer, and `formatDecimal` happens to print it "0.00" rather than "-0.00"
 * because `toFixed` drops the sign, so there is no live defect to fix here.
 * Returning a true 0 is what keeps it that way: the band no longer depends on
 * a sign the formatter is silently discarding, and neither does any test that
 * compares against 0.
 */
export function roundScore(value: number): number {
  const rounded = Math.round(value * 100) / 100
  return rounded === 0 ? 0 : rounded
}

/** Why an otherwise complete set of inputs cannot be scored. */
export type Smaf5Problem = "plateletsUnit" | "bodyOutOfRange" | "outOfRange"

/**
 * The first reason `m` cannot be scored, or null when it can.
 *
 * The order is the order of the messages' usefulness, not the order of the
 * fields, and each message names the boxes the reader can actually act on:
 *
 *   - A platelet count above the accepted maximum comes first and gets its own
 *     message. It is the single most likely mistake on this page, since the
 *     same count is written 250 in Switzerland and 250,000 in the American
 *     convention, and "check your entries" would send a reader looking for a
 *     typo that is not there. It is also the most dangerous one to wave
 *     through: platelets subtract, so a thousandfold-too-large count drives
 *     the score to roughly -1700 and would print a reassuring "Low risk".
 *   - Then height and weight, together with the BMI they imply, because a
 *     reader can only fix this by correcting one of those two boxes.
 *   - Then the plain plausibility bounds on the remaining values.
 */
export function smaf5Problem(m: Smaf5Measurements): Smaf5Problem | null {
  if (m.platelets > MAX_PLATELETS) {
    return "plateletsUnit"
  }
  const bmi = bmiFrom(m.heightCm, m.weightKg)
  if (
    m.heightCm < MIN_HEIGHT_CM ||
    m.heightCm > MAX_HEIGHT_CM ||
    m.weightKg < MIN_WEIGHT_KG ||
    m.weightKg > MAX_WEIGHT_KG ||
    bmi < MIN_BMI ||
    bmi > MAX_BMI
  ) {
    return "bodyOutOfRange"
  }
  if (
    m.ast < MIN_AST ||
    m.ast > MAX_AST ||
    m.platelets < MIN_PLATELETS
  ) {
    return "outOfRange"
  }
  return null
}

/**
 * Whether this BMI was raised to the floor before being scored, and so needs
 * the note beside the result. See `BMI_FLOOR`.
 */
export function smaf5IsBmiFloored(bmi: number): boolean {
  return bmi < BMI_FLOOR
}

// --- Result bands -----------------------------------------------------------

export type Smaf5Band = Smaf5BandDef["id"]

/**
 * The three sMAF-5 result bands, low to high, with the numbers the abstract
 * reports for each. `smaf5BandFor` walks this in order.
 *
 * Every cell of the page's reference table hangs off one of these entries:
 * the cut-off, the share of people in that band whose scan showed a liver
 * stiffness of 8 kPa or more, and the share who went on to a liver-related
 * event. That is what makes the table worth printing at all. A band is a
 * verdict on a population, and the population rates are the only honest way to
 * say what "high risk" means.
 *
 * Unlike FIB-4 there is no age split: sMAF-5 carries no age term and one set
 * of cut-offs serves every adult, which is much of the point. FIB-4's lower
 * cut-off has to move at 65 precisely because age multiplies its formula.
 */
const BAND_DEFS = [
  {
    id: "low",
    labelKey: k.bandLow,
    meaningKey: k.meaningLow,
    thresholdKey: k.thrLow,
    stiffnessKey: k.stiffnessLow,
    eventsKey: k.eventsLow,
  },
  {
    id: "intermediate",
    labelKey: k.bandIntermediate,
    meaningKey: k.meaningIntermediate,
    thresholdKey: k.thrIntermediate,
    stiffnessKey: k.stiffnessIntermediate,
    eventsKey: k.eventsIntermediate,
  },
  {
    id: "high",
    labelKey: k.bandHigh,
    meaningKey: k.meaningHigh,
    thresholdKey: k.thrHigh,
    stiffnessKey: k.stiffnessHigh,
    eventsKey: k.eventsHigh,
  },
] as const

export type Smaf5BandDef = (typeof BAND_DEFS)[number]

export const BAND_ORDER: readonly Smaf5BandDef[] = BAND_DEFS

export const SMAF5_BANDS: Record<Smaf5Band, Smaf5BandDef> = Object.fromEntries(
  BAND_DEFS.map((b) => [b.id, b])
) as Record<Smaf5Band, Smaf5BandDef>

/**
 * Which band a score falls into.
 *
 * The two cut-offs are NOT symmetrical, and the asymmetry is the abstract's,
 * not a rounding convenience: low risk is quoted as "below 0" and high risk as
 * "1 and above", which leaves the intermediate band closed at the bottom and
 * open at the top. So a score of exactly 0.00 is intermediate, and a score of
 * exactly 1.00 is high. Both are pinned in the unit test, because "both
 * cut-offs exclusive" is the shape a reader of the FIB-4 module would expect
 * and it is wrong here.
 *
 * Pass a value from `roundScore` (see the note there).
 */
export function smaf5BandFor(score: number): Smaf5Band {
  if (score >= HIGH_FROM) return "high"
  if (score < LOW_BELOW) return "low"
  return "intermediate"
}
